// Sidebar badges = "there is work waiting for you ON THIS PAGE".
//
// This replaces the old behaviour where every sidebar link inherited the count
// of unreviewed `admin_activity_events` for its broad section — which made
// sibling pages (Classes / Staffing / Enrollment Alerts, Instructors / Tax
// Center, Registrations / Bring a Friend, every Studio page) display the same
// number even though they had nothing to do with each other.
//
// Rules baked into this hook:
//   • one query per page, scoped to that page's own actionable records
//   • activity (a registration happened, an interest form arrived, an email
//     was logged) never becomes a badge — it stays in History / activity feeds
//   • if a page has no trustworthy actionable source, it gets NO badge
//   • if a query fails we show no badge and warn — never fall back to the old
//     broad section count, because a misleading badge is worse than none
//
// Nothing here writes, marks reviewed, or mutates any record.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/** Sidebar destinations that can carry a numbered badge. */
export type ActionBadgeKey =
  | "waiting_on"
  | "classes"
  | "enrollment_alerts"
  | "registrations"
  | "open_house"
  | "instructors"
  | "payroll"
  | "ledger";

export type ActionBadges = Partial<Record<ActionBadgeKey, number>>;

/**
 * Human-readable meaning of each badge — used for tooltips / aria labels.
 * The sidebar bubble now counts items the signed-in admin has NOT reviewed yet
 * (see src/hooks/useAdminUnseen.tsx), so the wording says "unreviewed".
 */
export const BADGE_MEANING: Record<ActionBadgeKey, string> = {
  waiting_on: "unreviewed Waiting On items",
  classes: "unreviewed classes open for registration with no assigned instructor",
  enrollment_alerts: "unreviewed enrollment alerts",
  registrations: "unreviewed checkouts where a parent was charged but enrollment failed",
  open_house: "unreviewed open house registrations",
  instructors: "unreviewed instructor proposals / onboarding packets",
  payroll: "unreviewed payroll periods due or past due",
  ledger: "unreviewed financial reconciliation findings",
};


const LIVE_ASSIGNMENT_STATUSES = ["active", "upcoming", "future", "current"];
const ACTIVE_WAITING_STATUSES = [
  "waiting",
  "reminder_scheduled",
  "reminder_sent",
  "response_received",
  "escalated",
  "overdue",
];

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

/** Runs one page's count. On failure: no badge (undefined) + a console warning. */
async function safeCount(key: ActionBadgeKey, fn: () => Promise<number>): Promise<number | undefined> {
  try {
    return await fn();
  } catch (e) {
    console.warn(`[admin badges] "${key}" actionable count unavailable — showing no badge.`, e);
    return undefined;
  }
}

async function head(table: string, build: (q: any) => any): Promise<number> {
  const q = build((supabase.from(table as any) as any).select("id", { count: "exact", head: true }));
  const { count, error } = await q;
  if (error) throw error;
  return count ?? 0;
}

/* ------------------------- per-page actionable counts ------------------------- */

/** Classes published + open for paid registration that have nobody teaching them. */
async function classesNeedingInstructor(): Promise<number> {
  const { data: rows, error } = await supabase
    .from("classes")
    .select("id, slug")
    .eq("status", "published")
    .eq("accepts_registrations", true)
    .or("hidden.is.null,hidden.eq.false");
  if (error) throw error;
  const open = rows ?? [];
  if (!open.length) return 0;
  const { data: assigns, error: aErr } = await supabase
    .from("instructor_class_assignments")
    .select("class_id, class_slug, status, end_date")
    .in("status", LIVE_ASSIGNMENT_STATUSES);
  if (aErr) throw aErr;
  const stamp = today();
  const staffed = new Set<string>();
  for (const a of assigns ?? []) {
    if (a.end_date && a.end_date < stamp) continue;
    if (a.class_id) staffed.add(a.class_id);
    if (a.class_slug) staffed.add(a.class_slug);
  }
  return open.filter((c) => !staffed.has(c.id) && !staffed.has(c.slug)).length;
}

/**
 * Enrollment decisions Savanna still has to make: a below-minimum checkpoint
 * with no admin action recorded, or a high-interest alert not yet cleared.
 */
async function enrollmentDecisions(): Promise<number> {
  const below = await head("enrollment_min_checks", (q) =>
    q.eq("below_minimum", true).eq("admin_action", "none"),
  );
  const interest = await head("class_interest_alerts", (q) => q.is("cleared_at", null));
  return below + interest;
}

/** Money left the parent but the seat never landed — always worth a human. */
async function brokenCheckouts(): Promise<number> {
  return head("checkout_failure_log", (q) =>
    q.eq("parent_charged", true).not("retry_succeeded", "is", true),
  );
}

async function openHouseNeedsAttention(): Promise<number> {
  const toReview = await head("open_house_registrations", (q) => q.eq("review_status", "new"));
  const followUp = await head("open_house_registrations", (q) => q.eq("follow_up_status", "needed"));
  return toReview + followUp;
}

async function instructorReviewQueue(): Promise<number> {
  const proposals = await head("instructor_proposals", (q) => q.eq("status", "New"));
  const packets = await head("instructor_onboarding_packets", (q) =>
    q.eq("overall_status", "submitted_for_review"),
  );
  return proposals + packets;
}

/** Payroll periods that are open and due (or overdue) within two weeks. */
async function payrollDue(): Promise<number> {
  return head("payroll_periods", (q) => q.neq("status", "closed").lte("pay_date", inDays(14)));
}

async function ledgerFindings(): Promise<number> {
  const { data, error } = await supabase
    .from("financial_integrity_state")
    .select("needs_reconciliation, unresolved_count");
  if (error) throw error;
  return (data ?? []).filter((r) => r.needs_reconciliation || (r.unresolved_count ?? 0) > 0).length;
}

async function waitingOpen(): Promise<number> {
  return head("waiting_items", (q) => q.in("status", ACTIVE_WAITING_STATUSES));
}

/* --------------------------------- the hook --------------------------------- */

export function useAdminActionBadges(enabled: boolean = true) {
  const [badges, setBadges] = useState<ActionBadges>({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const entries = await Promise.all([
      safeCount("waiting_on", waitingOpen).then((v) => ["waiting_on", v] as const),
      safeCount("classes", classesNeedingInstructor).then((v) => ["classes", v] as const),
      safeCount("enrollment_alerts", enrollmentDecisions).then((v) => ["enrollment_alerts", v] as const),
      safeCount("registrations", brokenCheckouts).then((v) => ["registrations", v] as const),
      safeCount("open_house", openHouseNeedsAttention).then((v) => ["open_house", v] as const),
      safeCount("instructors", instructorReviewQueue).then((v) => ["instructors", v] as const),
      safeCount("payroll", payrollDue).then((v) => ["payroll", v] as const),
      safeCount("ledger", ledgerFindings).then((v) => ["ledger", v] as const),
    ]);
    const next: ActionBadges = {};
    for (const [k, v] of entries) if (typeof v === "number") next[k] = v;
    setBadges(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    // Keep badges honest while an admin works: the tables that feed them are
    // small and the queries are head-only counts.
    const channel = supabase
      .channel(`admin-action-badges-${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "waiting_items" }, () => void refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "instructor_class_assignments" }, () => void refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "enrollment_min_checks" }, () => void refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "class_interest_alerts" }, () => void refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "instructor_proposals" }, () => void refresh())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled, refresh]);

  return { badges, loading, refresh };
}
